/**
 * Two-step "⌘⌫ to delete" logic for the command palette, pulled out as a pure
 * reducer so the destructive-action flow is unit-testable without rendering.
 *
 * Deleting a chat thread is irreversible (the backend hard-deletes the
 * conversation and purges its messages, attachments, chunks, and files), so a
 * single keystroke must never delete: the first press *arms* the highlighted
 * row, a second press on the SAME row confirms. Any other interaction
 * (navigating rows, editing the query, closing the palette) disarms.
 */

/** Minimal view of a palette row the delete reducer needs. */
export interface DeletableTarget {
  id: string;
  /** True only for rows that carry an `onDelete` handler (thread rows). */
  deletable: boolean;
}

export interface DeletePressResult {
  /** The row id awaiting confirmation, or `null` when nothing is armed. */
  armedId: string | null;
  /** Whether this press should execute the delete now. */
  fire: boolean;
}

/**
 * Resolve a ⌘⌫ press (or a click on the row's delete control) against the
 * currently-armed row and the press target.
 *
 *  - non-deletable / missing target → disarm, never fire
 *  - target already armed           → fire + disarm
 *  - otherwise                      → arm the target, don't fire
 */
export function pressDelete(
  armedId: string | null,
  target: DeletableTarget | undefined,
): DeletePressResult {
  if (!target || !target.deletable) {
    return { armedId: null, fire: false };
  }
  if (armedId === target.id) {
    return { armedId: null, fire: true };
  }
  return { armedId: target.id, fire: false };
}

/** Whether a ⌘⌫ / Ctrl+⌫ (or Delete) keystroke is a "delete highlighted" press. */
export function isDeleteChord(e: {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
}): boolean {
  return (e.metaKey || e.ctrlKey) && (e.key === "Backspace" || e.key === "Delete");
}
